import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Hexagon, Mail, Key, ArrowLeft, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PasswordResetPage() {
  const [step, setStep] = useState('request'); // request | confirm | done
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState('');

  const handleRequest = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/auth/password-reset/request`, { email });
      if (data.reset_token) setResetToken(data.reset_token);
      setStep('confirm');
    } catch (err) {
      setError(err.response?.data?.detail || 'Request failed');
    } finally { setLoading(false); }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/password-reset/confirm`, { token: token || resetToken, new_password: newPassword });
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.detail || 'Reset failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--nexus-bg-base)]" style={{ backgroundImage: 'radial-gradient(600px circle at 50% 30%, rgba(6,182,212,0.06), transparent 55%)' }}>
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-[var(--nexus-accent)] bg-opacity-15 flex items-center justify-center mb-4">
            <Hexagon size={28} className="text-[var(--nexus-accent)]" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-[var(--nexus-text-primary)]">Password Reset</h1>
        </div>

        <div className="nexus-card relative overflow-hidden p-6">
          {step === 'request' && (
            <form onSubmit={handleRequest} className="space-y-4">
              <p className="text-sm text-[var(--nexus-text-secondary)]">Enter your email to receive a password reset link.</p>
              {error && <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-300">{error}</div>}
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] font-medium">Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                  className="w-full mt-1 px-3 py-2.5 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-sm text-[var(--nexus-text-primary)] outline-none focus:border-[var(--nexus-accent)]" placeholder="your@email.com" />
              </div>
              <button type="submit" disabled={loading} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[var(--nexus-primary-bright)] text-white text-sm font-medium disabled:opacity-50">
                <Mail size={14} /> {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          )}

          {step === 'confirm' && (
            <form onSubmit={handleConfirm} className="space-y-4">
              <p className="text-sm text-[var(--nexus-text-secondary)]">Enter the reset token and your new password.</p>
              {error && <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-300">{error}</div>}
              {resetToken && <div className="p-2 rounded bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]"><p className="text-[10px] text-[var(--nexus-text-muted)]">Reset Token (demo):</p><p className="font-mono text-[10px] text-[var(--nexus-accent)] break-all">{resetToken}</p></div>}
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] font-medium">Reset Token</label>
                <input type="text" value={token || resetToken} onChange={e => setToken(e.target.value)} required
                  className="w-full mt-1 px-3 py-2.5 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-sm text-[var(--nexus-text-primary)] outline-none focus:border-[var(--nexus-accent)]" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] font-medium">New Password</label>
                <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={8}
                  className="w-full mt-1 px-3 py-2.5 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-sm text-[var(--nexus-text-primary)] outline-none focus:border-[var(--nexus-accent)]" placeholder="Min 8 chars, 1 letter, 1 number" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--nexus-text-muted)] font-medium">Confirm Password</label>
                <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required
                  className="w-full mt-1 px-3 py-2.5 rounded-lg bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)] text-sm text-[var(--nexus-text-primary)] outline-none focus:border-[var(--nexus-accent)]" />
              </div>
              <button type="submit" disabled={loading} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[var(--nexus-primary-bright)] text-white text-sm font-medium disabled:opacity-50">
                <Key size={14} /> {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          )}

          {step === 'done' && (
            <div className="text-center space-y-4">
              <CheckCircle size={48} className="text-[var(--nexus-success)] mx-auto" />
              <h3 className="text-lg font-semibold text-[var(--nexus-text-primary)]">Password Reset Successfully</h3>
              <p className="text-sm text-[var(--nexus-text-secondary)]">You can now sign in with your new password.</p>
              <Link to="/login" className="inline-block px-4 py-2 rounded-lg bg-[var(--nexus-primary-bright)] text-white text-sm font-medium">Sign In</Link>
            </div>
          )}

          <div className="mt-4 text-center">
            <Link to="/login" className="text-xs text-[var(--nexus-accent)] hover:underline flex items-center justify-center gap-1">
              <ArrowLeft size={12} /> Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
